import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { apiService } from '../services/api';
import { PriorityDto } from '../types';
import { Button } from '../components/ui/button';
import DataGrid from '../components/grid/DataGrid';
import { Edit, Trash2, Plus, Tag } from 'lucide-react';
import { toast } from 'react-hot-toast';
import ConfirmDialog from '../components/modals/ConfirmDialog';
import ColorPicker from '../components/ui/ColorPicker';
import Modal from '../components/ui/Modal';

const prioritySchema = yup.object({
  name: yup.string().required('Name is required').max(100, 'Name must be less than 100 characters'),
  code: yup.string().max(50, 'Code must be less than 50 characters').nullable(),
  description: yup.string().max(500, 'Description must be less than 500 characters').nullable(),
  colorCode: yup.string().required('Color is required'),
  sortOrder: yup.number().min(0, 'Sort order must be 0 or greater').required('Sort order is required'),
  isActive: yup.boolean().required('Active status is required')
});

type PriorityFormData = yup.InferType<typeof prioritySchema>;

const PrioritiesAdminPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<PriorityDto | null>(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset
  } = useForm<PriorityFormData>({
    resolver: yupResolver(prioritySchema),
    defaultValues: {
      name: '',
      code: '',
      description: '',
      colorCode: '#6B7280',
      sortOrder: 0,
      isActive: true
    }
  });

  const { data, isLoading } = useQuery({
    queryKey: ['priorities'],
    queryFn: () => apiService.getPriorities(),
  });

  const priorities: PriorityDto[] = (data?.data || []).filter(p => p.name.toLowerCase().includes(search.toLowerCase()));

  const createMutation = useMutation({
    mutationFn: (data: PriorityFormData) => apiService.createPriority({
      name: data.name,
      code: data.code || undefined,
      description: data.description || undefined,
      colorCode: data.colorCode,
      sortOrder: data.sortOrder,
      isActive: data.isActive
    }),
    onSuccess: () => {
      toast.success('Priority created');
      queryClient.invalidateQueries({ queryKey: ['priorities'] });
      setIsModalOpen(false);
      reset();
    },
    onError: () => toast.error('Failed to create priority')
  });

  const updateMutation = useMutation({
    mutationFn: (data: PriorityFormData) => apiService.updatePriority(editing!.id, {
      name: data.name,
      code: data.code || undefined,
      description: data.description || undefined,
      colorCode: data.colorCode,
      sortOrder: data.sortOrder,
      isActive: data.isActive
    }),
    onSuccess: () => {
      toast.success('Priority updated');
      queryClient.invalidateQueries({ queryKey: ['priorities'] });
      setIsModalOpen(false);
      setEditing(null);
      reset();
    },
    onError: () => toast.error('Failed to update priority')
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.deletePriority(id),
    onSuccess: () => {
      toast.success('Priority deleted');
      queryClient.invalidateQueries({ queryKey: ['priorities'] });
    },
    onError: () => toast.error('Failed to delete priority')
  });

  const columns = useMemo(() => [
    { id: 'name', header: 'Name', sortable: true, cell: (_: any, r: PriorityDto) => <span className="text-sm text-gray-900 dark:text-gray-100">{r.name}</span> },
    { id: 'code', header: 'Code', sortable: true, cell: (_: any, r: PriorityDto) => <span className="text-sm text-gray-600 dark:text-gray-300">{r.code || '-'}</span> },
    { 
      id: 'colorCode', 
      header: 'Color', 
      sortable: false, 
      cell: (_: any, r: PriorityDto) => (
        <div className="flex items-center space-x-2">
          <div 
            className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600" 
            style={{ backgroundColor: r.colorCode }}
          />
          <span className="text-xs text-gray-600 dark:text-gray-400 font-mono">{r.colorCode}</span>
        </div>
      )
    },
    { id: 'sortOrder', header: 'Order', sortable: true, cell: (_: any, r: PriorityDto) => <span className="text-sm text-gray-900 dark:text-gray-100">{r.sortOrder}</span> },
    { 
      id: 'isActive', 
      header: 'Active', 
      sortable: true, 
      cell: (_: any, r: PriorityDto) => (
        <div className="relative group">
          <div className="group-hover:opacity-0 transition-opacity duration-200">
            <span className={`px-2 py-0.5 rounded-full text-xs ${r.isActive ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>
              {r.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-end gap-2 pr-2" onClick={(e)=>e.stopPropagation()}>
            <button 
              onClick={() => {
                setEditing(r);
                reset({
                  name: r.name,
                  code: r.code || '',
                  description: r.description || '',
                  colorCode: r.colorCode,
                  sortOrder: r.sortOrder,
                  isActive: r.isActive
                });
                setIsModalOpen(true);
              }}
              className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
              title="Edit Priority"
            >
              <Edit className="h-4 w-4"/>
            </button>
            <button 
              onClick={() => {
                setSelectedToDelete(r);
                setShowDeleteConfirm(true);
              }}
              className="h-7 px-2 inline-flex items-center border rounded-md text-red-600 border-red-300 hover:bg-red-50"
              title="Delete Priority"
            >
              <Trash2 className="h-4 w-4"/>
            </button>
          </div>
        </div>
      )
    }
  ], []);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedToDelete, setSelectedToDelete] = useState<PriorityDto | null>(null);

  const confirmDelete = () => {
    if (!selectedToDelete) return;
    deleteMutation.mutate(selectedToDelete.id, {
      onSettled: () => {
        setShowDeleteConfirm(false);
        setSelectedToDelete(null);
      }
    });
  };

  const onSubmit = (data: PriorityFormData) => {
    if (editing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Tag className="h-8 w-8 text-gray-600 dark:text-gray-400" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Priorities</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Manage priority levels used across work orders</p>
          </div>
        </div>
        <Button onClick={() => { 
          setEditing(null); 
          reset({
            name: '',
            code: '',
            description: '',
            colorCode: '#6B7280',
            sortOrder: 0,
            isActive: true
          }); 
          setIsModalOpen(true); 
        }}>
          <Plus className="h-4 w-4 mr-2" /> New Priority
        </Button>
      </div>

      <div className="bg-white shadow rounded-lg">
        <DataGrid 
          data={priorities} 
          columns={columns} 
          isLoading={isLoading} 
          searchTerm={search} 
          onSearch={setSearch} 
          currentPage={1} 
          pageSize={priorities.length || 10} 
          totalCount={priorities.length} 
          totalPages={1} 
          onPageChange={() => {}} 
          searchPlaceholder="Search priorities..." 
          emptyMessage="No priorities found" 
          getRowClassName={() => 'group'}
          onRowClick={(row) => {
            const r = row as PriorityDto;
            setEditing(r);
            reset({
              name: r.name,
              code: r.code || '',
              description: r.description || '',
              colorCode: r.colorCode,
              sortOrder: r.sortOrder,
              isActive: r.isActive
            });
            setIsModalOpen(true);
          }}
          rowTooltip="Edit Priority"
        />
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditing(null); }}
        title={editing ? 'Edit Priority' : 'New Priority'}
        maxWidth="lg"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Active checkbox at the top */}
                <div className="md:col-span-2 flex items-center gap-2 pb-4 border-b border-gray-200">
                  <Controller
                    name="isActive"
                    control={control}
                    render={({ field }) => (
                      <>
                        <input 
                          id="isActive" 
                          type="checkbox" 
                          checked={field.value} 
                          onChange={e => field.onChange(e.target.checked)}
                          className="h-4 w-4 text-accent-color focus:ring-accent-color border-gray-300 dark:border-gray-600 rounded"
                        />
                        <label htmlFor="isActive" className="text-sm text-gray-700 dark:text-gray-300">Active</label>
                      </>
                    )}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    {...register('name')}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                      errors.name ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                    }`}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.name.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Code (optional)</label>
                  <input
                    type="text"
                    {...register('code')}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                      errors.code ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                    }`}
                  />
                  {errors.code && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.code.message}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                  <input
                    type="text"
                    {...register('description')}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                      errors.description ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                    }`}
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.description.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sort Order</label>
                  <input
                    type="number"
                    {...register('sortOrder', { valueAsNumber: true })}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                      errors.sortOrder ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                    }`}
                  />
                  {errors.sortOrder && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.sortOrder.message}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <Controller
                    name="colorCode"
                    control={control}
                    render={({ field }) => (
                      <ColorPicker
                        value={field.value}
                        onChange={field.onChange}
                        label="Color"
                      />
                    )}
                  />
                  {errors.colorCode && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.colorCode.message}</p>
                  )}
                </div>
              </div>

          <div className="mt-6 flex justify-end gap-2 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => { setIsModalOpen(false); setEditing(null); }}
              className="inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-100 dark:ring-gray-600 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-accent-color"
            >
              Cancel
            </button>
            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>{editing ? 'Save Changes' : 'Create Priority'}</Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => { setShowDeleteConfirm(false); setSelectedToDelete(null); }}
        onConfirm={confirmDelete}
        title="Delete Priority"
        message={`Are you sure you want to delete "${selectedToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};

export default PrioritiesAdminPage;
